import { useEffect, useState } from "react";
import { Users, MapPin, Globe2 } from "lucide-react";
import { PortalLayout } from "../../components/PortalLayout";
import { api } from "../../lib/api";

export default function Shortlist() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/employer/shortlists").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  return (
    <PortalLayout>
      <div data-testid="shortlist-page">
        <p className="text-xs uppercase tracking-[0.25em] text-[#7C8514] font-medium">Recruiter-curated</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] tracking-tight mt-2">Your shortlist</h1>
        <p className="text-sm text-[#51637a] mt-1">Only candidates our recruiters have specifically shared with you appear here.</p>

        {items === null ? (
          <div className="flex justify-center py-20"><div className="h-8 w-8 rounded-full border-2 border-[#0d2f4d] border-t-transparent animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="ditch-card mt-8 p-12 text-center max-w-xl" data-testid="shortlist-empty-state">
            <Users className="mx-auto text-[#e3e0d3]" size={40} />
            <h2 className="font-heading font-bold text-lg text-[#0d2f4d] mt-5">No shortlisted candidates yet</h2>
            <p className="text-sm text-[#51637a] mt-2 leading-relaxed">
              Once our recruiters have vetted candidates for your roles, they'll appear here.
              Awaiting recruiter review — we'll be in touch soon.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            {items.map(({ shortlist, candidate, role }) => (
              <div key={shortlist.id} className="ditch-card p-6" data-testid={`shortlist-card-${shortlist.id}`}>
                {role && <span className="rounded-full bg-[#f5f3ee] text-[#0d2f4d] text-xs font-medium px-3 py-1">{role.title}</span>}
                <h3 className="font-heading font-bold text-[#0d2f4d] text-lg mt-3">
                  {candidate ? `${candidate.first_name} ${candidate.last_name_initial}.` : "Candidate"}
                </h3>
                {candidate?.headline && <p className="text-sm text-[#51637a] mt-1">{candidate.headline}</p>}
                <div className="mt-3 space-y-1.5 text-xs text-[#51637a]">
                  {candidate?.location && <p className="flex items-center gap-1.5"><MapPin size={13} /> {candidate.location}</p>}
                  {candidate?.country_of_origin && <p className="flex items-center gap-1.5"><Globe2 size={13} /> From {candidate.country_of_origin}</p>}
                </div>
                {candidate?.skills?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {candidate.skills.map((s) => (
                      <span key={s} className="rounded-full border border-[#e3e0d3] text-xs px-2.5 py-0.5 text-[#0d2f4d]">{s}</span>
                    ))}
                  </div>
                )}
                {candidate?.next_role && (candidate.next_role.desired_role || candidate.next_role.locations || candidate.next_role.salary || candidate.next_role.availability) && (
                  <div className="mt-4 text-xs text-[#51637a] bg-[#C1CC24]/10 border border-[#C1CC24]/50 rounded-md p-3 space-y-1">
                    <p className="font-medium text-[#0d2f4d]">Looking for</p>
                    {candidate.next_role.desired_role && <p>Role: {candidate.next_role.desired_role}</p>}
                    {candidate.next_role.locations && <p>Location: {candidate.next_role.locations}</p>}
                    {candidate.next_role.salary && <p>Salary: {candidate.next_role.salary}</p>}
                    {candidate.next_role.availability && <p>Availability: {candidate.next_role.availability}</p>}
                  </div>
                )}
                {shortlist.notes && (
                  <p className="mt-4 text-xs text-[#51637a] bg-[#fcfbf8] rounded-md p-3 leading-relaxed">
                    <span className="font-medium text-[#0d2f4d]">Recruiter note:</span> {shortlist.notes}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
